import React from 'react';
import { AlertTriangle, CheckCircle2, Route } from 'lucide-react';
import { Badge } from '../ui/badge';
import { fmtDate, memberName } from './constants';
import { GanttTimeline } from './GanttTimeline';

const Stat = ({ label, value, tone = '', testid }) => (
  <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-1 text-lg font-bold ${tone}`}>{value}</p>
  </div>
);

export const CriticalPathPanel = ({ cpm, activities, members, onOpen }) => {
  if (!activities.length) return <p className="py-8 text-center text-sm text-slate-500" data-testid="cpm-empty">Agrega actividades con fechas y dependencias para calcular la ruta crítica.</p>;
  const rows = [...activities].sort((a, b) => cpm.activities[a.id].early_start.localeCompare(cpm.activities[b.id].early_start));
  return (
    <div className="space-y-5" data-testid="cpm-panel">
      {cpm.alert ? (
        <div className="flex items-center gap-3 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300" data-testid="cpm-alert">
          <AlertTriangle className="h-5 w-5 shrink-0" />La ruta crítica termina el <b>{fmtDate(cpm.estimated_finish)}</b>, {cpm.delay_days} días después de la fecha fin del proyecto ({fmtDate(cpm.project_end)}).
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300" data-testid="cpm-ok">
          <CheckCircle2 className="h-5 w-5 shrink-0" />La ruta crítica cabe dentro del plazo{cpm.project_end ? ` (fin ${fmtDate(cpm.project_end)})` : ''}.
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Fin estimado (CPM)" value={fmtDate(cpm.estimated_finish)} testid="cpm-estimated-finish" />
        <Stat label="Duración total" value={`${cpm.duration_days} días`} />
        <Stat label="Actividades críticas" value={`${cpm.critical_path.length} / ${activities.length}`} tone="text-red-600 dark:text-red-400" testid="cpm-critical-count" />
        <Stat label="Atraso vs plazo" value={`${cpm.delay_days} días`} tone={cpm.alert ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'} />
      </div>
      <p className="flex flex-wrap items-center gap-2 text-sm" data-testid="cpm-path"><Route className="h-4 w-4 text-red-500" /><b>Ruta crítica:</b>
        {cpm.critical_path.map((id, i) => <span key={id} className="text-slate-700 dark:text-slate-300">{i > 0 && '→ '}{cpm.activities[id].title}</span>)}</p>
      <GanttTimeline activities={activities} members={members} onOpen={onOpen} cpm={cpm} />
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm" data-testid="cpm-table">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/50">
            <tr><th className="p-2">Actividad</th><th className="p-2">Duración</th><th className="p-2">Inicio temprano</th><th className="p-2">Fin temprano</th><th className="p-2">Inicio tardío</th><th className="p-2">Fin tardío</th><th className="p-2">Holgura</th><th className="p-2" /></tr>
          </thead>
          <tbody>{rows.map((a) => {
            const x = cpm.activities[a.id];
            return (
              <tr key={a.id} className={`border-t border-slate-200 dark:border-slate-700 ${x.critical ? 'bg-red-50/60 dark:bg-red-950/20' : ''}`} data-testid={`cpm-row-${a.id}`}>
                <td className="p-2"><p className="font-medium">{a.title}</p><p className="text-[11px] text-slate-500">{memberName(members, a.assignee_user_id)}</p></td>
                <td className="p-2">{x.duration} d</td><td className="p-2">{fmtDate(x.early_start)}</td><td className="p-2">{fmtDate(x.early_finish)}</td>
                <td className="p-2">{fmtDate(x.late_start)}</td><td className="p-2">{fmtDate(x.late_finish)}</td>
                <td className="p-2 font-mono" data-testid={`cpm-slack-${a.id}`}>{x.slack_days} d</td>
                <td className="p-2">{x.critical && <Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">Crítica</Badge>}
                  {x.shift_days > 0 && <span className="ml-1 text-[11px] text-amber-600" title="Las dependencias desplazan su inicio">+{x.shift_days} d por dependencias</span>}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    </div>
  );
};
